import { action, makeAutoObservable, runInAction } from "mobx";
import CommonStore from "../../../stores/CommonStore";
import ArticleDelivery from "../../../entities/ArticleDelivery";
import OrderAdditionalPrice from "../../../entities/OrderAdditionalPriceRequest";
import { ArticleService } from "../../../service/articleDelivery/ArticleService";
import ArticleWithPriceResponse from "../../../model/articleDelivery/ArticleWithPriceResponse";
import ClientContact from "../../../entities/ClientContact";
import { ArticleDeliveryService } from "../../../service/articleDelivery/ArticleDeliveryService";
import ArticleDeliveryOrderRequest from "../../../model/articleDelivery/ArticleDeliveryOrderRequest";
import Client from "../../../entities/Client";
import Agency from "../../../entities/Agency";
import Seller from "../../../entities/Seller";
import Geo from "../../../entities/Geo";
import ArticlePrice from "../../../entities/ArticlePrice";
import AddPaymentRequest from "../../../model/payment/AddPaymentRequest";
import { AppStorage, AppStorageValues } from "../../../util/AppStorage";
import ArticleCategory from "../../../entities/productOrder/article/ArticleCategory";
import UserStore from "../../../stores/UserStore";
import Access from "../../Access/AllowAccess/Access";

class NewArticleDeliveryStore {

    public isModalOpen: boolean;
    public validating: boolean;
    public commonStore: CommonStore;
    public userStore: UserStore;
    public response?: ArticleDelivery[]
    public errorKey?: string;

    public finishButtonLoading = false;

    public activeStep = 0;
    public createAnother = false;

    // Remittances data

    // Articles
    public loadingArticles: boolean;
    public articles?: ArticleWithPriceResponse[];
    public selectedArticles: ArticleWithPriceResponse[];
    public selectedArticlesPrices: ArticlePrice[];
    public nameFilter?: string;
    public geoFilter?: Geo;
    public categoryFilter?: ArticleCategory;

    // Contacts
    public selectedClient?: Client;
    public selectedContact?: ClientContact;
    public selectedAgency?: Agency;
    public selectedSeller?: Seller;
    
    public observations?: string;

    // Additional prices
    public additionalPrices: OrderAdditionalPrice[];

    // Payments
    public payments: AddPaymentRequest[];
    public paymentCompleted: boolean;

    public validationgErrorKey?: string;

    constructor(commonStore: CommonStore, userStore: UserStore) {
        this.isModalOpen = false;
        this.validating = false;
        this.loadingArticles = false;
        this.selectedArticles = [];
        this.selectedArticlesPrices = [];
        this.additionalPrices = [];
        this.payments = [];
        this.paymentCompleted = false;
        this.userStore = userStore;
        this.commonStore = commonStore;
        this.selectedAgency = AppStorage.get(AppStorageValues.SELECTED_AGENCY, undefined);
        this.selectedSeller = AppStorage.get(AppStorageValues.SELECTED_SELLER, undefined);
        makeAutoObservable(this);
    }

    public reset() {
        this.loadingArticles = false;
        this.selectedArticles = [];
        this.selectedArticlesPrices = [];
        this.selectedClient = undefined;
        this.selectedContact = undefined;
        this.additionalPrices = [];
        this.payments = [];
        this.paymentCompleted = false;
        this.activeStep = 0;
        this.observations = undefined;
        this.validationgErrorKey = undefined;
    }

    public resetToCreateAnother() {
        this.loadingArticles = false;
        this.selectedContact = undefined;
        this.additionalPrices = [];
        this.selectedArticles = [];
        this.selectedArticlesPrices = [];
        this.payments = [];
        this.paymentCompleted = false;
        this.validationgErrorKey = undefined;
    }

    @action
    public async openModal() {
        this.reset();
        this.isModalOpen = true;
    }

    @action
    public closeModal() {
        this.isModalOpen = false;
    }

    @action
    public restart() {
        this.response = undefined;
    }

    // Contact area

    @action
    public setClient(client?: Client) {
        this.selectedClient = client;
    }

    @action
    public setContact(contact?: ClientContact) {
        this.selectedContact = contact;
    }

    @action
    public setObservations(observations?: string) {
        this.observations = observations;
    }

    // Articles area

    @action
    public async filterByGeo(): Promise<void> {
        this.loadingArticles = true;
        const response = await ArticleService.filter(this.geoFilter ? this.geoFilter.id : '00001', this.nameFilter ?? '', this.categoryFilter?.id, this.selectedAgency?.id);
        if (response) {
            runInAction(() => {
                this.articles = response || [];
            })
        }
        this.loadingArticles = false;
    }

    get isEmpty(): boolean {
        return !this.articles || this.articles.length === 0;
    }

    @action
    public async selectArticle(articleId: string) {
        const article = this.articles!.find(t => t.id === articleId);
        if (article && this.selectedArticles && this.selectedArticles.findIndex(t => t.id === articleId) < 0) {
            this.selectedArticles?.push(article);
        }
    }

    @action
    public async deselectArticle(articleId: string) {
        this.selectedArticles = this.selectedArticles?.filter(t => t.id !== articleId);
        this.selectedArticlesPrices = this.selectedArticlesPrices?.filter(t => t.articleId !== articleId);
    }

    // Additional prices area

    @action
    public addAdditionalPrice(currency: string, amount: number, comment: string) {
        this.additionalPrices.push(new OrderAdditionalPrice(amount, comment, false));
    }

    @action
    public removeAdditionalPrice(additionalPrice: OrderAdditionalPrice) {
        this.additionalPrices = this.additionalPrices.filter(t => t.amount !== additionalPrice.amount && t.comment !== additionalPrice.comment);
    }

    public getAdditionalPriceTotal(): number {
        return this.additionalPrices.reduce((sum, current) => sum + current.amount, 0);
    }

    // Payment area

    public getServicePrice(): number {
        return this.selectedArticlesPrices?.reduce((total, obj) =>
            total + (obj.price * (this.selectedArticles.find(article => article.id === obj.articleId)!!.amount)), 0) || 0;
    }

    public getTotalToPay(): number {
        return this.getServicePrice() + this.getAdditionalPriceTotal();
    }

    @action
    public setPayments(payments: AddPaymentRequest[], paymentCompleted: boolean) {
        this.payments = payments;
        this.paymentCompleted = paymentCompleted;
    }

    @action
    public async createArticleDelivery() {
        if (!this.selectedContact) {
            await this.commonStore.showErrorToast('Debe seleccionar un contacto.');
            return;
        }
        if (!this.selectedArticles || this.selectedArticles.length === 0) {
            await this.commonStore.showErrorToast('Debe seleccionar algún artículo.');
            return;
        }
        if (this.finishButtonLoading) {
            return;
        }
        this.finishButtonLoading = true;
        const response = await ArticleDeliveryService.create(
            new ArticleDeliveryOrderRequest(
                this.selectedContact.clientId,
                this.selectedContact?.id,
                this.getTotalToPay(),
                this.selectedArticles.map(article => {
                    const { price, deliveryTimeDays } = this.selectedArticlesPrices.find(price => price.articleId === article.id) || {};
                    return {
                        articleId: article.id,
                        amount: article.amount,
                        price: price ?? 0,
                        deliveryTimeDays: deliveryTimeDays
                    }
                }),
                this.payments,
                this.additionalPrices,
                this.selectedAgency?.id,
                this.selectedSeller?.id,
                this.observations));
        if (response.success && response.data) {
            if (!this.createAnother) {
                this.closeModal();
                window.location.href = '/article-remittances';
            } else {
                this.resetToCreateAnother();
                this.activeStep = 
                    this.userStore.hasAccess(Access.SEND_ORDERS_TO_AGENCIES) && this.userStore.hasAccess(Access.LIST_AGENCY) ?
                    1 : 0;
            }
        } else {
            this.validationgErrorKey = response.error;
        }
        this.finishButtonLoading = false;
    }

}

export default NewArticleDeliveryStore;